import type { RealProfile } from '../types/profiles';
import type { CharacterRanking, CharacterKind, RecognitionOutcome } from '../types/ai';

export interface AccuracyRanking {
  character: string;
  kind: CharacterKind;
  /** Média da % de confiança do modelo pras classificações desse caractere
   *  (ponderada pela contagem de cada label — ver computeRealAIAnalytics). */
  avgConfidencePct: number;
  totalClassifications: number;
}

export interface RealAIAnalytics {
  totalDrawings: number;
  recognizedCharacters: CharacterRanking[];
  typeDistribution: { letters: number; numbers: number; lettersPct: number; numbersPct: number };
  outcomeBreakdown: { direct: number; suggestive: number; total: number; directPct: number };
  mostSuggestiveCharacters: CharacterRanking[];
  mostDirectCharacters: CharacterRanking[];
  topLetters: CharacterRanking[];
  topNumbers: CharacterRanking[];
  confidenceIndex: number;
  /** Desenhos com melhor acurácia média do modelo, maior % primeiro — a %
   *  vem embutida no texto do label (não existe campo numérico separado,
   *  ver parseLabel), então "acurácia" aqui é a confiança do modelo na
   *  própria classificação, não uma correção verificada por humano (o app
   *  não registra isso em nenhum lugar). */
  bestAccuracy: AccuracyRanking[];
}

// Mensagens que o app devolve quando NÃO reconheceu nada de verdade (ver
// classifyBitmapNew/classifyBitmapOld em FreePaintActivity.kt) — nunca
// viram um "personagem" na listagem.
const NON_CLASSIFICATION_PATTERNS = [
  /não carregado/i,
  /nenhum desenho detectado/i,
  /não reconhecido/i,
  /desenho está muito fraco/i,
];

interface ParsedLabel {
  character: string;
  kind: CharacterKind;
  outcome: RecognitionOutcome;
  /** % de confiança do modelo pra ESSE registro — extraída do final do
   *  label ("... (67%)"). `null` nos labels do modelo antigo (lumo-lite),
   *  que não gravam confiança nenhuma. */
  confidencePct: number | null;
}

/**
 * O texto do label (FreePaintActivity.kt, classifyBitmapNew/Old no app) traz
 * a confiança do modelo embutida no final — não é um campo separado. Formatos
 * reais, todos terminando no CARACTERE reconhecido:
 *   "Isso se parece com o número X (NN%)"  — baixa confiança, dígito
 *   "Isso se parece com X (NN%)"           — baixa confiança, letra
 *   "Isso parece o número X (NN%)"         — alta confiança, dígito
 *   "Você desenhou X (NN%)"                — alta confiança, letra
 *   "Isso parece o número X"               — modelo antigo, dígito, sem %
 *   "Você desenhou um X"                   — modelo antigo, letra, sem %
 *
 * Bug corrigido aqui: a extração antiga pegava sempre a ÚLTIMA PALAVRA do
 * label pra achar o caractere — funcionava nos 2 formatos do modelo antigo
 * (sem sufixo), mas nos 4 formatos do modelo novo a última palavra virava
 * "(NN%)" (a % de confiança), não o caractere desenhado. Por isso a maioria
 * das telas mostrava porcentagem em vez do item reconhecido, e só os poucos
 * registros do modelo antigo apareciam certos. Fix: remove o "(NN%)" do
 * final ANTES de pegar a última palavra.
 *
 * O outcome (direct/suggestive) também estava invertido: comparava com
 * "Isso parece" (sem o "se"), que bate com o ramo de ALTA confiança
 * ("Isso parece o número X") — o de baixa confiança é "Isso SE parece com".
 */
function parseLabel(label: string): ParsedLabel | null {
  const trimmed = label.trim();
  if (NON_CLASSIFICATION_PATTERNS.some((p) => p.test(trimmed))) return null;

  const confMatch = trimmed.match(/\((\d{1,3})%\)\s*$/);
  const confidencePct = confMatch ? Number(confMatch[1]) : null;
  const withoutConf = confMatch ? trimmed.slice(0, confMatch.index).trim() : trimmed;

  const outcome: RecognitionOutcome = /^isso se parece com/i.test(withoutConf) ? 'suggestive' : 'direct';

  const character = withoutConf.split(' ').pop();
  if (!character) return null;
  const kind: CharacterKind = /^[0-9]$/.test(character) ? 'number' : 'letter';
  return { character, kind, outcome, confidencePct };
}

export function computeRealAIAnalytics(profiles: RealProfile[]): RealAIAnalytics {
  const byCharacter = new Map<string, { kind: CharacterKind; direct: number; suggestive: number }>();
  const accuracyByCharacter = new Map<string, { kind: CharacterKind; weightedSum: number; count: number }>();

  for (const profile of profiles) {
    for (const dm of profile.drawingMetrics) {
      const parsed = parseLabel(dm.label);
      if (!parsed) continue;
      const row = byCharacter.get(parsed.character) ?? { kind: parsed.kind, direct: 0, suggestive: 0 };
      if (parsed.outcome === 'direct') row.direct += dm.count;
      else row.suggestive += dm.count;
      byCharacter.set(parsed.character, row);

      if (parsed.confidencePct != null) {
        const acc = accuracyByCharacter.get(parsed.character) ?? { kind: parsed.kind, weightedSum: 0, count: 0 };
        acc.weightedSum += parsed.confidencePct * dm.count;
        acc.count += dm.count;
        accuracyByCharacter.set(parsed.character, acc);
      }
    }
  }

  const recognizedCharacters: CharacterRanking[] = [...byCharacter.entries()]
    .map(([character, row]) => ({ character, kind: row.kind, count: row.direct + row.suggestive }))
    .sort((a, b) => b.count - a.count);

  const totalDrawings = recognizedCharacters.reduce((sum, r) => sum + r.count, 0);
  const letters = recognizedCharacters.filter((r) => r.kind === 'letter').reduce((sum, r) => sum + r.count, 0);
  const numbers = recognizedCharacters.filter((r) => r.kind === 'number').reduce((sum, r) => sum + r.count, 0);

  const direct = [...byCharacter.values()].reduce((sum, r) => sum + r.direct, 0);
  const suggestive = [...byCharacter.values()].reduce((sum, r) => sum + r.suggestive, 0);
  const total = direct + suggestive;

  const mostSuggestiveCharacters = [...byCharacter.entries()]
    .map(([character, row]) => ({ character, kind: row.kind, count: row.suggestive }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);

  const mostDirectCharacters = [...byCharacter.entries()]
    .map(([character, row]) => ({ character, kind: row.kind, count: row.direct }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);

  const bestAccuracy: AccuracyRanking[] = [...accuracyByCharacter.entries()]
    .map(([character, row]) => ({
      character,
      kind: row.kind,
      avgConfidencePct: row.count > 0 ? Math.round(row.weightedSum / row.count) : 0,
      totalClassifications: row.count,
    }))
    .sort((a, b) => b.avgConfidencePct - a.avgConfidencePct);

  return {
    totalDrawings,
    recognizedCharacters,
    typeDistribution: {
      letters,
      numbers,
      lettersPct: totalDrawings > 0 ? Math.round((letters / totalDrawings) * 100) : 0,
      numbersPct: totalDrawings > 0 ? Math.round((numbers / totalDrawings) * 100) : 0,
    },
    outcomeBreakdown: {
      direct,
      suggestive,
      total,
      directPct: total > 0 ? Math.round((direct / total) * 100) : 0,
    },
    mostSuggestiveCharacters,
    mostDirectCharacters,
    topLetters: recognizedCharacters.filter((r) => r.kind === 'letter'),
    topNumbers: recognizedCharacters.filter((r) => r.kind === 'number'),
    confidenceIndex: total > 0 ? Math.round((direct / total) * 100) : 0,
    bestAccuracy,
  };
}
